/**
 * ============================================================
 * PAYLOAD RULES — shared distribution-policy screen
 * ============================================================
 *
 * The single source of truth for which file payloads the marketplace refuses to
 * publish. Shared by every surface that must agree on that policy:
 *
 *   - the processor's payload screen (`processor-lambda/src/components/payload-screen.ts`),
 *     the backend gate that deactivates a repo's catalog records when a push trips it; and
 *   - the web contribute uploader (`web` FileDropZone / ContributeSkillPage) and the
 *     github-proxy upload path (`github-proxy/src/upload.ts`), which must reject the
 *     SAME files *before* they are committed.
 *
 * Why the uploader must screen too (issue #369): web-contributed skills all live in
 * ONE shared monorepo (`contributed-skills`), each as `skills/<slug>/...`. The processor
 * screens the whole repo tree, so a single disqualifying file doesn't just fail its own
 * skill — the processor deactivates EVERY skill in the repo. Validating at the uploader
 * with these exact rules keeps junk out of the shared repo in the first place, and having
 * one rule definition keeps the UI and the backend gate from drifting.
 *
 * This is a distribution-policy gate for the company marketplace, deliberately STRICTER
 * than what Claude's plugin spec permits. The spec allows a plugin to ship binaries (a
 * `bin/` directory, a bundled MCP-server executable), but we do not redistribute those
 * enterprise-wide:
 *
 *   - compiled executables / native binaries — an opaque binary auto-installed across
 *     the company is a supply-chain / malware surface.
 *   - vendored dependency or build trees (node_modules/, dist/, …) — redistributes
 *     stale, potentially-vulnerable transitive deps with license baggage.
 *   - oversized files / payloads — abuse/cost vector, and blows the ~50 MB install ceiling.
 *
 * KNOWN LIMITATION (accepted): detection is by file path/extension only — the screen sees
 * the tree/file listing, not blob bytes. A native binary committed with NO extension (the
 * default form of a Unix ELF/Mach-O executable, e.g. `bin/agent`) is NOT caught. Closing
 * that would require magic-byte-sniffing every candidate blob, which this gate deliberately
 * avoids. Extension coverage below is a best-effort bar against the common, clearly-labeled
 * cases, not a guarantee.
 *
 * PATH-SHAPE INVARIANT (required for the shared surfaces to agree): every rule below MUST be
 * position-independent / depth-agnostic — it matches on a path SEGMENT (anchored `(?:^|\/)`
 * … `\/`) or a trailing extension, never on an absolute position via a leading `^`. This is
 * load-bearing because the surfaces screen DIFFERENT path shapes for the same file: the
 * uploader classifies skill-relative paths (`node_modules/x`, `SKILL.md`), while the
 * processor classifies the full committed tree, where the file is prefixed
 * `skills/<slug>/.claude-plugin/…`. A rule anchored to `^` (or otherwise sensitive to the
 * prefix) would classify the two shapes differently and silently reintroduce the
 * uploader-vs-backend drift this module exists to prevent — and the backend failure mode is
 * whole-repo deactivation, not a local reject. Keep new rules segment/extension based.
 */

export interface ScreenEntry {
  path: string;
  /** Blob size in bytes (0 when unknown). */
  size: number;
}

export type ScreenVerdict = { ok: true } | { ok: false; reason: string };

/** Any single file larger than this fails screening. Tunable. */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

/**
 * Total payload larger than this fails screening. Set to Claude's documented
 * ~50 MB plugin-upload ceiling so we never publish something that can't install.
 * Tunable.
 */
export const MAX_TOTAL_BYTES = 50 * 1024 * 1024;

// Compiled executables / native binaries, installer/disk-image formats, and
// compiled bytecode, matched on file extension.
//
// Deliberately EXCLUDES object/static-lib artifacts (`.o`, `.a`): they're build
// intermediates almost never committed to a skill repo, they aren't a realistic
// distribution vector the way exe/dll/so/dylib are, and — since a single match
// deactivates the ENTIRE repo (all sibling plugins) — the false-positive cost of
// two such generic extensions (`outline.o`, `notes.a`) outweighs their marginal
// protective value. `.so` (incl. versioned) is still caught below.
const EXECUTABLE_RE =
  /\.(?:exe|dll|dylib|bin|msi|app|deb|rpm|dmg|iso|pkg|jar|node|wasm|class|pyc)$/i;

// Shared objects, INCLUDING versioned suffixes: `libfoo.so`, `libc.so.6`,
// `libbar.so.1.2.3`. A plain `\.so$` (as before) let `libc.so.6` slip through.
const SHARED_OBJECT_RE = /\.so(?:\.\d+)*$/i;

export function isExecutablePath(path: string): boolean {
  return EXECUTABLE_RE.test(path) || SHARED_OBJECT_RE.test(path);
}

// Unambiguously-vendored directories: these are never legitimate skill/component
// NAMES, so they match as a path SEGMENT (leading `/` or start, trailing `/`) at
// any depth. A file merely *named* like one — e.g. `docs/build.md` — never trips
// a segment matcher because there's no trailing `/`.
//
// Case-INSENSITIVE, like the extension rules above: on a case-insensitive
// filesystem (macOS/Windows) a contributor can surface `Node_Modules/`, `Dist/`,
// `Build/`. Matching case-sensitively would let those slip past the screen — and
// past the uploader that shares these rules — silently weakening the control.
const VENDORED_DIR_RE = /(?:^|\/)(?:node_modules|\.next|__pycache__|venv|\.venv)\//i;

// `build`, `dist`, `target`, `vendor` are common build output BUT are also
// plausible skill/component names (`skills/build/SKILL.md` is a skill literally
// named "build"). Treat them as vendored EXCEPT when the segment is itself a
// component directory — i.e. it sits directly under a component root. That keeps
// `skills/build/…`, `plugins/x/skills/dist/…`, `agents/vendor/…` publishable
// while still flagging genuine build output at the repo root (`dist/…`) or nested
// inside a component (`skills/foo/build/…`). Case-insensitive for the same reason
// as VENDORED_DIR_RE.
const BUILD_DIR_RE = /(?:^|\/)(?:build|dist|target|vendor)\//i;
const COMPONENT_NAMED_BUILD_DIR_RE =
  /(?:^|\/)(?:skills|agents|commands|hooks|scripts|monitors|output-styles|themes|plugins)\/(?:build|dist|target|vendor)\//i;

export function isVendoredPath(path: string): boolean {
  if (VENDORED_DIR_RE.test(path)) return true;
  return BUILD_DIR_RE.test(path) && !COMPONENT_NAMED_BUILD_DIR_RE.test(path);
}

/** Which path-based rule a disallowed file tripped. */
export type PayloadRuleClass = "executable" | "vendored";

/**
 * The rule CLASS a path trips, or null if it's permitted. Coarser than
 * `classifyPath` (no per-path human string), so it's safe to log/emit as a
 * bounded telemetry tag — there are only two possible values, no raw paths.
 */
export function ruleClassFor(path: string): PayloadRuleClass | null {
  if (isExecutablePath(path)) return "executable";
  if (isVendoredPath(path)) return "vendored";
  return null;
}

/**
 * Classify a single file PATH against the path-based rules (executable / vendored).
 * Returns a human-readable reason if the path is disallowed, else null.
 *
 * Path-only by design: it needs just the path, so the uploader can flag files as
 * they're added — before any size is known — and can report EVERY offending file
 * rather than the first. Size limits are enforced separately (see `screenPayload`
 * in the processor, and the byte checks in the uploaders), since those need the
 * blob size, not the path.
 */
export function classifyPath(path: string): string | null {
  switch (ruleClassFor(path)) {
    case "executable":
      return `contains a compiled executable/native binary: ${path}`;
    case "vendored":
      return `contains a vendored dependency/build directory: ${path}`;
    default:
      return null;
  }
}

/**
 * The one canonical remediation sentence appended to every "these files can't be
 * published" message — the FileDropZone add-time warning, the ContributeSkillPage
 * submit guard, and the github-proxy 400 body. Lives here (not hand-written per
 * surface) so the list of blocked categories can't drift out of sync with the
 * rules above as they evolve. Each surface supplies its own lead-in; this is the
 * shared "why / what to remove" tail.
 */
export const DISALLOWED_PAYLOAD_REMEDIATION =
  "Compiled executables, bytecode (.pyc), and vendored or build folders " +
  "(node_modules, dist, __pycache__, …) can't be published.";

/**
 * The offending group a disallowed path collapses to for a rejection message.
 * A file rejected for living in a vendored/build directory collapses to that
 * directory (dragging a project folder surfaces the WHOLE tree — hundreds of
 * `node_modules/` files — issue #369), so the user sees "remove node_modules/",
 * not a wall of paths. An executable/bytecode match stands alone: the filename
 * itself is the actionable signal.
 */
function offendingGroup(path: string): string {
  const parts = path.split("/");
  for (let i = 0; i < parts.length - 1; i++) {
    const dir = `${parts.slice(0, i + 1).join("/")}/`;
    if (isVendoredPath(dir)) return dir;
  }
  return path;
}

/**
 * Build a human-readable, BOUNDED summary of disallowed paths for a rejection
 * message. Collapses files under a common vendored/build directory to a single
 * `dir/ (N files)` entry and caps the number of listed groups, so neither the UI
 * warning nor any HTTP error body built from it balloons when a contributor drops
 * a folder full of `node_modules`/`__pycache__` (the primary #369 trip). Returns
 * "" for an empty list (callers only build a message when there is a violation).
 */
export function summarizeDisallowedPaths(paths: string[], maxGroups = 10): string {
  const counts = new Map<string, number>();
  for (const p of paths) {
    const key = offendingGroup(p);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const groups = [...counts.entries()].map(([key, n]) =>
    key.endsWith("/") && n > 1 ? `${key} (${n} files)` : key,
  );

  if (groups.length <= maxGroups) return groups.join(", ");
  const shown = groups.slice(0, maxGroups);
  return `${shown.join(", ")}, …and ${groups.length - maxGroups} more`;
}
